import { useState, useEffect, useCallback, useRef } from 'react'
import { trackEvent } from '@/utils/analytics'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react'
import type { Core } from 'cytoscape'
import type { Node } from '@/service/graph'
import { parseName } from '@/service/graph'
import { useTranslation } from '@/i18n'

export const SEARCH_DEBOUNCE_MS = 300
// Clamp search zoom to a comfortable band: zoom in if too far out,
// but don't over-zoom if the user is already close.
export const SEARCH_ZOOM_MIN = 1.0
export const SEARCH_ZOOM_MAX = 1.5

interface GraphSearchProps {
  cyInstance: Core | null
  nodes: Map<string, Node>
  isOpen: boolean
  onClose: () => void
}

export default function GraphSearch({
  cyInstance,
  nodes,
  isOpen,
  onClose,
}: GraphSearchProps) {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [matchingNodeIds, setMatchingNodeIds] = useState<string[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Only pan/fit the view on user-initiated actions (typing a new term,
  // pressing next/prev). Node-map updates (drag, add, delete) must still
  // re-run the search so match highlights stay correct, but they must NOT
  // pan the viewport back to the first match.
  const shouldPanOnNextRunRef = useRef(false)

  // Mark pan-required whenever the user changes the search term (user action).
  // Node-map updates don't touch this flag, so drags won't pan the view.
  useEffect(() => {
    shouldPanOnNextRunRef.current = true
  }, [debouncedSearchTerm])

  // Search logic — keeps `nodes` as a dep so adds/deletes propagate into matches.
  const performSearch = useCallback(
    (term: string): string[] => {
      if (!term.trim()) return []

      const lowerTerm = term.toLowerCase()
      const matches: string[] = []

      nodes.forEach((node, nodeId) => {
        let searchableText = ''

        switch (node.type) {
          case 'symbol': {
            // Prefer symbol_type (ML detected type) over name for searching
            // Note: symbol_type can be the literal string 'null', not actual null
            const nameToUse = (node.symbol_type && node.symbol_type !== 'null') ? node.symbol_type : node.name
            const { label, category } = parseName(nameToUse)
            // Include the classification + user label so a manually-classified
            // object (e.g. a traced "Pit" tank) is findable by its Type.
            const classification = `${node.pnid_level_i ?? ''} ${node.pnid_level_ii ?? ''} ${node.pnid_level_iii ?? ''} ${node.label ?? ''}`
            // Search matches: detected type, label, category, classification
            searchableText = `${nameToUse} ${label} ${category} ${classification}`.toLowerCase()
            break
          }
          case 'tag':
            searchableText =
              `${node.content} ${node.pnid_level_i ?? ''} ${node.pnid_level_ii ?? ''} ${node.pnid_level_iii ?? ''} ${node.label ?? ''}`.toLowerCase()
            break
          case 'connection': {
            // Search the classifier's raw class name (note) plus the
            // resolved hierarchy so queries like "software", "pneumatic",
            // or "instrumentation" surface every classified line.
            const fields = [
              node.note,
              node.pnid_level_i,
              node.pnid_level_ii,
              node.pnid_level_iii,
            ]
            searchableText = fields.filter(Boolean).join(' ').toLowerCase()
            break
          }
        }

        if (searchableText.includes(lowerTerm)) {
          matches.push(nodeId)
        }
      })

      return matches
    },
    [nodes]
  )

  // Navigate to a specific match
  const goToMatch = useCallback(
    (index: number, nodeIds: string[]) => {
      if (!cyInstance || nodeIds.length === 0) return

      const nodeId = nodeIds[index]
      cyInstance.nodes().removeClass('search-current')
      cyInstance.$id(nodeId).addClass('search-current')
      cyInstance.animate({
        center: { eles: cyInstance.$id(nodeId) },
        zoom: Math.min(Math.max(cyInstance.zoom(), SEARCH_ZOOM_MIN), SEARCH_ZOOM_MAX),
        duration: 300,
      })
    },
    [cyInstance]
  )

  // Analytics: fire once per user-initiated term change, not on every
  // downstream node-map update that happens to re-run the search effect.
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      trackEvent('graph-search_executed', { query_length: debouncedSearchTerm.trim().length })
    }
  }, [debouncedSearchTerm])

  // Track the last dispatched matches set so we can skip redundant class
  // mutations when node-map updates don't actually change the match set
  // (avoids DOM thrash + pulse animation restart during drags).
  const lastAppliedMatchesRef = useRef<string>('')

  // Recompute matches whenever the search term OR the underlying nodes change
  // (so adds/deletes are reflected). Pan is gated behind shouldPanOnNextRunRef
  // so unrelated node-map updates (drag) don't yank the viewport.
  useEffect(() => {
    const matches = performSearch(debouncedSearchTerm)
    setMatchingNodeIds((prev) => {
      if (
        prev.length === matches.length &&
        prev.every((id: string, i: number) => id === matches[i])
      ) {
        return prev
      }
      return matches
    })

    if (!cyInstance) return

    // When the pan flag is set, the user just changed the search term and
    // the index-reset effect is scheduling currentMatchIndex → 0 in the
    // same commit. Using the stale currentMatchIndex here would pan/highlight
    // the wrong node before the reset lands, so force index 0 in that case.
    const isNewTermRun = shouldPanOnNextRunRef.current
    const effectiveIdx =
      matches.length === 0
        ? -1
        : isNewTermRun
        ? 0
        : Math.min(currentMatchIndex, matches.length - 1)
    const effectiveId = effectiveIdx === -1 ? '' : matches[effectiveIdx]
    const appliedSig = `${effectiveId}|${matches.join(',')}`
    const classesStale = appliedSig !== lastAppliedMatchesRef.current

    if (classesStale) {
      lastAppliedMatchesRef.current = appliedSig
      cyInstance.nodes().removeClass('search-match search-current')
      if (matches.length > 0) {
        matches.forEach((id) => cyInstance.$id(id).addClass('search-match'))
        cyInstance.$id(effectiveId).addClass('search-current')
      }
    }

    if (isNewTermRun && matches.length > 0) {
      shouldPanOnNextRunRef.current = false
      cyInstance.animate({
        center: { eles: cyInstance.$id(effectiveId) },
        zoom: Math.min(Math.max(cyInstance.zoom(), SEARCH_ZOOM_MIN), SEARCH_ZOOM_MAX),
        duration: 300,
      })
    }
  }, [debouncedSearchTerm, cyInstance, performSearch, currentMatchIndex])

  // Reset match index only when the user types a new search term, not on
  // every node-map update that causes performSearch to rebuild.
  useEffect(() => {
    setCurrentMatchIndex(0)
  }, [debouncedSearchTerm])

  const goToNextMatch = useCallback(() => {
    if (matchingNodeIds.length === 0) return
    const newIndex = (currentMatchIndex + 1) % matchingNodeIds.length
    setCurrentMatchIndex(newIndex)
    goToMatch(newIndex, matchingNodeIds)
  }, [currentMatchIndex, matchingNodeIds, goToMatch])

  const goToPreviousMatch = useCallback(() => {
    if (matchingNodeIds.length === 0) return
    const newIndex =
      (currentMatchIndex - 1 + matchingNodeIds.length) % matchingNodeIds.length
    setCurrentMatchIndex(newIndex)
    goToMatch(newIndex, matchingNodeIds)
  }, [currentMatchIndex, matchingNodeIds, goToMatch])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && matchingNodeIds.length > 0) {
      e.preventDefault()
      if (e.shiftKey) goToPreviousMatch()
      else goToNextMatch()
    }
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  const handleClose = useCallback(() => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setMatchingNodeIds([])
    setCurrentMatchIndex(0)
    lastAppliedMatchesRef.current = ''
    if (cyInstance) {
      cyInstance.nodes().removeClass('search-match search-current')
    }
    onClose()
  }, [cyInstance, onClose])

  if (!isOpen) return null

  return (
    <div className="absolute top-20 left-4 z-50 bg-white dark:bg-dark-card p-3 rounded-lg shadow-lg border border-gray-200 dark:border-dark-border flex items-center gap-2 min-w-[320px]">
      <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      <Input
        ref={inputRef}
        type="text"
        placeholder={t('graph_editor:search.nodesPlaceholder', { defaultValue: 'Search nodes...' })}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        className="flex-1 h-8 text-sm border-0 shadow-none focus-visible:ring-0"
      />

      {searchTerm && (
        <span className="text-xs text-gray-500 dark:text-dark-text-muted whitespace-nowrap">
          {matchingNodeIds.length > 0
            ? `${currentMatchIndex + 1}/${matchingNodeIds.length}`
            : t('graph_editor:search.noMatches', { defaultValue: 'No matches' })}
        </span>
      )}

      {matchingNodeIds.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPreviousMatch}
            className="h-8 w-8 p-0"
            title={t('graph_editor:search.previousMatch', { defaultValue: 'Previous match (Shift+Enter)' })}
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextMatch}
            className="h-8 w-8 p-0"
            title={t('graph_editor:search.nextMatch', { defaultValue: 'Next match (Enter)' })}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
        </>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={handleClose}
        className="h-8 w-8 p-0"
        title={t('graph_editor:search.close', { defaultValue: 'Close (Escape)' })}
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  )
}
